import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useCreateAdHocPlay } from "@/queries/plays";
import { useReportContext } from "@/stores/ReportContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  AdHocPlayForm,
  AdHocPlayFormSubmission,
  AdHocPlayFormValues,
  adHocPlaySchema,
} from "./AdHocPlayForm";

export function CreateAdHocPlayButton() {
  const [open, setOpen] = useState(false);
  const { reportId } = useReportContext();
  const { mutate: createPlay, isPending } = useCreateAdHocPlay(reportId);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<AdHocPlayFormValues>({
    resolver: zodResolver(adHocPlaySchema),
    defaultValues: {
      title: "",
      category: "General",
      location_type: "gate",
      location_details: "",
      terminal: null,
      concourse: null,
      start_interval: 0,
      end_interval: 0,
      actions: [{ title: "", team_id: null, order: 0 }],
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: AdHocPlayFormSubmission) => {
    try {
      const filteredActions = data.actions
        .filter((action) => action.title.trim() !== "")
        .map((action, index) => ({
          title: action.title,
          team_id: action.team_id,
          order: action.order !== undefined ? action.order : index,
        }));

      const playData = {
        title: data.title,
        category: data.category,
        location_type: data.location_type,
        location_details: data.location_details || "",
        terminal: data.terminal || "",
        concourse: data.concourse || "",
        start_interval: data.start_interval,
        end_interval: data.end_interval,
        target_date: data.target_date,
        actions: filteredActions,
      };

      createPlay(playData, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "One-time play created successfully.",
          });
          queryClient.invalidateQueries({ queryKey: ["plays"] });
          form.reset();
          setOpen(false);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Error creating play",
            description: error.message,
          });
        },
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error creating play",
        description: "An unexpected error occurred.",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="default"
          className="bg-green-700 hover:bg-green-700 text-white"
        >
          <Plus className="h-4 w-4" /> Create One-Time Play
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1000px] bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100">
            Create One-Time Play
          </DialogTitle>
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground" />
        </DialogHeader>
        <div className="py-4">
          <AdHocPlayForm
            form={form}
            onSubmit={onSubmit}
            isLoading={isPending}
            submitButtonText="Create Play"
            onClose={() => setOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
